/**
 * studentLiveViewModel — l'état du live de l'élève (le moteur, `engine/live/student`) en props des
 * pages et des feuilles du design system : `<StudentLivePage>`, `<LiveChat>`, `<LiveHelpSheet>`,
 * `<LiveTutorAbsentDialog>`.
 *
 * Pur : les gestes sont au crochet du moteur, l'écran les branche.
 */
import { type AutoModeState } from "../../../../../engine/live/student/autoMode"
import { type LiveAutoModeSheetProps } from "../../../../../design-system/student"
import moment from "moment"

import { GROUP_CHANNEL, type LiveChatChannel } from "../../../../../engine/live/student/liveChatChannels"
import { type MicGateView } from "../../../../../engine/live/student/micGate"
import {
    type MicBlocker,
    type MicRefusalStep,
    type MicRefusalView,
} from "../../../../../engine/live/student/micRefusal"
import {
    permissionPathFor,
    promptPlaceOf,
    systemPermissionPath,
    type PromptPlace,
} from "../../../../../engine/precall/browserPermissionPaths"
import { browserLabel } from "../../../../../engine/precall/deviceCheckCopy"
import { type PermissionPromptKind } from "../../../../../engine/precall/permissionPrompt"
import { type UIGuidanceLine } from "../../../../../design-system/molecules/DeviceCheckGuidance"
import { frenchSpacing } from "../../../../../design-system/utils/frenchSpacing"
import { toGuidanceLine } from "../LiveEntry/deviceCheckViewModel"
import { type LiveChatMessage } from "../../../../../engine/live/student/useLiveChat"
import { type TutorAbsentDialog, type TutorWaiting } from "../../../../../engine/live/student/useTutorWaiting"
import {
    messengerFailure,
    sortBySentAt,
    toUIConversationMessage,
} from "../../../adapters/student/Messaging/studentConversationAdapter"
import { type ApiLiveRosterMember } from "../../../adapters/student/Live/studentLiveAdapter"
import { groupConversation } from "../../../ui/formatters/conversationFormatter"
import { type UILiveSession, type UITutor } from "../../../ui/types"
import { type UIConversationDay, type UIConversationMessage } from "../../../ui/types/studentConversation"
import {
    type UILiveProblem,
    type UILiveSamLine,
    type UILiveWaiting,
    type UISoundMode,
} from "../../../ui/types/studentLive"

export function liveSoundMode(dialogue: boolean): UISoundMode {
    return dialogue ? "dialogue" : "focused"
}

/** L'attente du tuteur → la barre pleine largeur sous l'en-tête. */
export function liveWaiting(waiting: TutorWaiting | null): UILiveWaiting | null {
    if (waiting == null) return null
    return {
        reason: waiting.reason,
        ...(waiting.arrival != null ? { arrival: waiting.arrival } : {}),
        late: waiting.late,
    }
}

/**
 * Ce que Sam dit dans sa bulle — jamais en Dialogue (Sam n'y est pas), et chaque phrase se tait une fois
 * refermée par sa croix :
 *  1. le masteur pas encore là : Sam propose de commencer (`waiting`) ;
 *  2. le masteur là, l'élève sans aucune activité, ouverte ou relue, ni faite pendant ce live : Sam dit
 *     que son masteur est là (`first_activity`, Wajih 22/10 : sans elle, personne ne lui parlait et il ne
 *     comprenait pas qu'un masteur était là). Le masteur absent sans attente annoncée (un cours
 *     individuel, la connexion, après la proposition de remplacement), Sam ne le dit pas ;
 *  3. le passage en Concentré d'un cours partagé (`shared_focus`).
 */
export function liveSamLine(args: {
    dialogue: boolean
    waiting: boolean
    /** Le masteur est entré dans le live. */
    tutorPresent: boolean
    /** Une activité ouverte, ou une activité faite relue. */
    hasActivity: boolean
    /** L'élève a fini (ou arrêté) une activité pendant ce live : la prochaine n'est plus la première. */
    finishedSome: boolean
    /** Le passage en Concentré vient d'être dit (`samBubble` du moteur). */
    focusedJustNow: boolean
    sharedCourse: boolean
    /** Les phrases refermées par leur croix (l'attente, la première activité). */
    closed: ReadonlySet<UILiveSamLine>
}): UILiveSamLine | null {
    if (args.dialogue) return null
    if (args.waiting) return args.closed.has("waiting") ? null : "waiting"
    if (args.tutorPresent && !args.hasActivity && !args.finishedSome && !args.closed.has("first_activity")) {
        return "first_activity"
    }
    return args.focusedJustNow && args.sharedCourse ? "shared_focus" : null
}

/* ────────────────────────────── le chat ────────────────────────────── */

/**
 * Le fil du chat : les messages de la messagerie (`CommonChat.transformMessage`), du plus ancien au
 * plus récent, chacun une fois, avec leur état d'envoi. Dans « Tous », chaque message d'un autre
 * élève dit son auteur par son visage (`sender`), jamais par son prénom ; le tuteur garde la place de
 * l'interlocuteur.
 */
export function liveChatDays(args: {
    messages: LiveChatMessage[]
    selfId: string | number | null | undefined
    channel: LiveChatChannel
    roster: ApiLiveRosterMember[]
    apiUrl: string
    avatarOf: (path: string | null | undefined) => string | undefined
}): UIConversationDay[] {
    const { messages, selfId, channel, roster, apiUrl, avatarOf } = args
    const byId = new Map(roster.map((member) => [String(member.id), member]))
    const rows: UIConversationMessage[] = []
    const seen = new Set<string>()
    for (const raw of messages) {
        // Un envoi d'ici (en cours ou en échec) montre sa pièce par son adresse locale, sans l'API devant.
        const message = toUIConversationMessage(raw, selfId, raw.status != null ? "" : apiUrl)
        if (message == null || seen.has(message.id)) continue
        seen.add(message.id)
        // Un refus du serveur dit son motif et ne se renvoie pas : la règle de la messagerie.
        const failure =
            raw.status === "failed" ? messengerFailure(raw.failure, raw.image != null || raw.document != null) : null
        const withStatus: UIConversationMessage =
            failure != null
                ? {
                      ...message,
                      status: "failed",
                      ...(failure.reason != null ? { failureReason: failure.reason } : {}),
                      ...(failure.retryable ? {} : { retryable: false }),
                  }
                : raw.status === "sending"
                  ? { ...message, status: "sending" }
                  : message
        if (channel === GROUP_CHANNEL && withStatus.author === "other" && raw.owner_id != null) {
            const author = byId.get(String(raw.owner_id))
            rows.push({
                ...withStatus,
                sender: {
                    id: String(raw.owner_id),
                    ...(avatarOf(author?.avatar) != null ? { avatarUrl: avatarOf(author?.avatar) } : {}),
                    ...(author?.is_tutor === true ? { tutor: true } : {}),
                },
            })
        } else {
            rows.push(withStatus)
        }
    }
    return groupConversation(sortBySentAt(rows))
}

/**
 * « Camille écrit… » : seulement le masteur du fil ouvert (celui de sa puce, celui de la séance dans
 * « Tous ») — un autre élève ne se nomme jamais.
 */
export function liveChatTyping(
    typingId: string | null,
    tutor: ApiLiveRosterMember | null | undefined
): string | undefined {
    if (typingId == null || tutor?.id == null || typingId !== String(tutor.id)) return undefined
    return `${tutor.display_name?.split(" ")[0] ?? "Ton masteur"} écrit…`
}

/* ─────────────────────────── le mode autonome ─────────────────────────── */

/** Le mode autonome du moteur → la feuille : l'étape, les propositions et leur ligne. */
export function liveAutoModeSheet(
    state: AutoModeState,
    subjectName: (id: number | string | null | undefined, name: string | null | undefined) => string | null
): Omit<LiveAutoModeSheetProps, "onClose" | "onEvaluate" | "onChoose" | "onReplay" | "onRaiseHand"> | null {
    if (state.phase === "closed") return null
    if (state.phase === "evaluating") return { visible: true, step: "evaluate" }
    const expected = state.evaluation === "lost" ? 2 : 3
    return {
        visible: true,
        step: "choose",
        evaluation: state.evaluation,
        proposals: state.proposals.map((proposal) => {
            const { material } = proposal
            const subject = subjectName(material.school_subject?.id, material.school_subject?.name)
            const detail = [
                subject,
                material.estimated_duration != null ? `${material.estimated_duration} min` : null,
                material.level != null && material.level !== "" ? `Niveau ${material.level}` : null,
            ]
                .filter((part): part is string => part != null && part !== "")
                .join(" · ")
            return {
                id: String(material.id),
                intent: proposal.intent,
                ...(detail !== "" ? { detail } : {}),
            }
        }),
        pendingCount: state.loading ? Math.max(0, expected - state.proposals.length) : 0,
        failed: state.error,
        starting: state.starting,
        startFailed: state.startError,
    }
}

/* ─────────────────────────── « Un problème ? » ─────────────────────────── */

export type LiveHelpContext = {
    dialogue: boolean
    micOn: boolean
    cameraOn: boolean
    poorConnection: boolean
    web: boolean
    /** Le micro refusé à l'app (`false`), autorisé (`true`) ; `null` ou absent : on ne sait pas. */
    micAllowed?: boolean | null
    /** Qui refuse le micro, quand un refus a été vu en séance : l'ordinateur a son propre chemin. */
    micBlocker?: MicBlocker
    /** En Dialogue, le micro ouvert ne capte aucun son : coupé par l'ordinateur (`micSilence`). */
    micSilent?: boolean
    /** La caméra, de même. */
    cameraAllowed?: boolean | null
    /** Qui refuse la caméra, quand un refus a été vu en séance. */
    cameraBlocker?: MicBlocker
}

type Step = {
    iconName:
        | "microphone"
        | "keyboard"
        | "headphones"
        | "window-close"
        | "volume-up"
        | "redo"
        | "video"
        | "wifi"
        | "times-circle"
        | "comment"
    text: string
    detail?: string
}

/**
 * Ce que l'écran a vérifié pour un problème, et ce qu'on peut essayer : les mots de l'ancien
 * panneau (`TroubleshootingPanel.js`). Le mode Concentré, qui coupe le son, se dit d'abord.
 */
export function liveHelpDiagnostic(
    problem: UILiveProblem | null,
    context: LiveHelpContext
): { notice?: string; steps: Step[] } {
    const focusedNotice = "Mode Concentré : le son est coupé, c'est normal. Lève la main pour repasser en Dialogue."
    const restart: Step = context.web
        ? { iconName: "redo", text: "Rafraîchis la page", detail: "Parfois un simple rechargement résout le problème" }
        : {
              iconName: "redo",
              text: "Ferme et rouvre l'application",
              detail: "Parfois un simple redémarrage résout le problème",
          }
    switch (problem) {
        case "mic":
            return {
                notice:
                    context.micAllowed === false
                        ? blockedNotice("micro", context.web, context.micBlocker)
                        : !context.dialogue
                          ? focusedNotice
                          : !context.micOn
                            ? "Ton micro est coupé. Touche le micro, en bas, pour le rallumer."
                            : context.micSilent === true
                              ? MIC_SILENT_NOTICE
                              : undefined,
                steps: [
                    {
                        iconName: "keyboard",
                        text: "Certains claviers ont une touche qui coupe le micro",
                        detail: "Vérifie le bouton mute de ton clavier",
                    },
                    {
                        iconName: "headphones",
                        text: "Essaie de brancher ou débrancher des écouteurs",
                        detail: "Cela peut changer le micro utilisé",
                    },
                    {
                        iconName: "window-close",
                        text: "Ferme les autres applis qui utilisent le micro",
                        detail: "Snap, Teams, Zoom, FaceTime...",
                    },
                    restart,
                ],
            }
        case "cant_hear":
            return {
                notice: !context.dialogue ? focusedNotice : undefined,
                steps: [
                    { iconName: "volume-up", text: "Monte le volume de ton appareil" },
                    {
                        iconName: "headphones",
                        text: "Essaie de brancher ou débrancher des écouteurs",
                        detail: "Cela peut changer le haut-parleur utilisé",
                    },
                    restart,
                ],
            }
        case "camera":
            return {
                notice:
                    context.cameraAllowed === false
                        ? blockedNotice("caméra", context.web, context.cameraBlocker)
                        : !context.cameraOn
                          ? "Ta caméra est coupée. Touche la caméra, en bas, pour la rallumer."
                          : undefined,
                steps: [
                    {
                        iconName: "video",
                        text: "Vérifie que ta caméra n'est pas couverte",
                        detail: "Certains ordinateurs ont un cache physique",
                    },
                    {
                        iconName: "window-close",
                        text: "Ferme les applis qui utilisent la caméra",
                        detail: "Snap, Teams, Zoom, FaceTime...",
                    },
                    restart,
                ],
            }
        case "connection":
            return {
                notice: context.poorConnection
                    ? "Ta connexion est instable. Essaie ceci :"
                    : "Ta connexion semble bonne. Si ça coupe encore, essaie ceci :",
                steps: [
                    {
                        iconName: "wifi",
                        text: "Rapproche-toi de ta box Wi-Fi",
                        detail: "Plus tu es proche, meilleur est le signal",
                    },
                    {
                        iconName: "times-circle",
                        text: "Ferme les autres applis et onglets",
                        detail: "Jeux, YouTube, réseaux sociaux...",
                    },
                    context.web
                        ? {
                              iconName: "wifi",
                              text: "Branche-toi en câble si possible",
                              detail: "Le câble Ethernet est plus stable que le Wi-Fi",
                          }
                        : { iconName: "wifi", text: "Passe en données mobiles (4G/5G)" },
                ],
            }
        default:
            return {
                steps: [{ iconName: "comment", text: "Lève la main ou écris à ton masteur : il t'aide." }, restart],
            }
    }
}

/** Le micro ouvert qui ne capte rien : la phrase de l'ancien diagnostic (`MicDiagnostic`), en clair. */
const MIC_SILENT_NOTICE =
    "Ton micro ne capte aucun son : il est sans doute coupé sur ton ordinateur. Vérifie la touche muet de ton clavier, ou le son de ton ordinateur."

/** Ce que l'app a mesuré d'un appareil refusé : où l'autoriser. */
function blockedNotice(device: "micro" | "caméra", web: boolean, blocker: MicBlocker = "browser"): string {
    const article = device === "micro" ? "ton micro" : "ta caméra"
    const allow = device === "micro" ? "Autorise-le" : "Autorise-la"
    if (web && blocker === "system") {
        return `Ton ordinateur bloque ${article}. Ouvre ses Réglages, puis Confidentialité, autorise ton navigateur, puis rafraîchis la page.`
    }
    return web
        ? `Ton navigateur bloque ${article}. ${allow} dans les réglages du site, puis rafraîchis la page.`
        : `L'app n'a pas accès à ${article}. ${allow} dans les réglages de ton téléphone.`
}

/* ─────────────────────────── le micro à demander ─────────────────────────── */

/** Le masteur que la phrase nomme : son prénom, sinon « ton masteur ». */
function tutorWho(tutorFirstname: string | null | undefined): string {
    return tutorFirstname != null && tutorFirstname.trim() !== "" ? tutorFirstname.trim() : "ton masteur"
}

/**
 * Le micro tombé (`micDrop`) : en Dialogue, il s'est coupé sans geste de l'élève (un casque débranché, un
 * micro repris par une autre app). La salle essaie de le rouvrir ; le bandeau dit ce que le masteur vit et
 * porte le geste qui le redemande.
 */
export function liveMicDroppedCopy(tutorFirstname: string | null | undefined): { message: string; action: string } {
    const who = tutorWho(tutorFirstname)
    return {
        message: frenchSpacing(`Ton micro s'est coupé : ${who} ne t'entend plus.`),
        action: "Rouvrir mon micro",
    }
}

/**
 * « Active ton micro » (`micGate`) : en Dialogue, quand le micro attend le geste de l'élève — le live
 * rouvert par un rechargement, sans que la page l'ait obtenu. Le bouton le demande dans le toucher ; la
 * phrase dit pourquoi, et que le navigateur va poser la question.
 */
export function liveMicAskCopy(tutorFirstname: string | null | undefined): {
    title: string
    message: string
    confirmLabel: string
    cancelLabel: string
} {
    const who = tutorWho(tutorFirstname)
    return {
        title: "Active ton micro",
        message: frenchSpacing(
            `Pour que ${who} t'entende, ton navigateur a besoin de ton accord. Touche « Activer mon micro », puis choisis « Autoriser ».`
        ),
        confirmLabel: "Activer mon micro",
        cancelLabel: "Plus tard",
    }
}

/**
 * Ce que le micro montre, dans l'ordre : tant qu'une demande attend la réponse du navigateur, rien que
 * son guidage (`LivePermissionCoach`) — même fermé par « OK » ; puis le refus, puis la demande. La boîte
 * du son mis en pause s'efface derrière l'un comme l'autre (`covered`).
 */
export function liveMicSurface(
    gate: MicGateView,
    refusal: MicRefusalView,
    prompting: boolean
): { dialog: "refused" | "ask" | null; covered: boolean } {
    const dialog = prompting ? null : refusal.visible ? "refused" : gate.ask ? "ask" : null
    return { dialog, covered: prompting || dialog != null }
}

/* ─────────────────────── la question du navigateur ─────────────────────── */

export type PermissionCoachCopy = {
    title: string
    message: string
    okLabel: string
    /** Là où la question s'ouvre ; `null` au téléphone, où elle est au milieu de l'écran. */
    arrow: PromptPlace
}

const PROMPT_ASKS: Record<PermissionPromptKind, { title: string; so: string }> = {
    microphone: { title: "Autorise ton micro", so: "t'entende" },
    camera: { title: "Autorise ta caméra", so: "te voie" },
    both: { title: "Autorise ton micro et ta caméra", so: "t'entende et te voie" },
}

/**
 * Le guidage de la question du navigateur (`usePermissionPrompt`), le voile de WhatsApp Web : une phrase
 * qui dit quoi choisir et pour qui, une flèche vers la question. Au téléphone, la question est au milieu
 * de l'écran : « Touche », et pas de flèche ni de « en haut ».
 */
export function permissionCoachCopy(
    kind: PermissionPromptKind,
    tutorFirstname: string | null | undefined,
    browser: string
): PermissionCoachCopy {
    const who = tutorWho(tutorFirstname)
    const arrow = promptPlaceOf(browser)
    const ask = PROMPT_ASKS[kind]
    return {
        title: ask.title,
        message: frenchSpacing(
            arrow != null
                ? `Clique sur « Autoriser », en haut, pour que ${who} ${ask.so}.`
                : `Touche « Autoriser » pour que ${who} ${ask.so}.`
        ),
        okLabel: "OK",
        arrow,
    }
}

/* ─────────────────────────── le micro refusé ─────────────────────────── */

export type LiveMicRefusedCopy = {
    title: string
    message: string
    /** Le chemin du déblocage, un geste par ligne (au web). */
    steps?: UIGuidanceLine[]
    confirmLabel: string
    cancelLabel: string
    /** Le second bouton : « Plus tard » ferme, ou « Réessayer » redemande. */
    cancel: "later" | "retry"
}

/**
 * La boîte du micro refusé (`micRefusal`), à son étape : « Réessayer » (`retry`, au téléphone), puis le
 * chemin des réglages (`settings`, d'emblée au web).
 *
 * **Au web**, le guide du déblocage : le chemin du navigateur (`browserPermissionPaths`, celui du test
 * d'entrée), un geste par ligne avec l'icône à chercher, ou celui de l'ordinateur quand c'est sa
 * confidentialité qui bloque ; puis « Recharger la page », qui efface le refus du document — sortir du
 * live et y revenir ne le fait pas — et « Plus tard », la sortie d'une boîte qui couvre le live. Le
 * micro de la barre redemande.
 *
 * **Au téléphone**, rien ne change : « Réessayer », qui redemande dans le geste ; refusé encore, ses
 * Réglages. Une phrase par geste, le verbe en premier ; les espaces de la ponctuation, insécables
 * (`frenchSpacing`).
 */
export function liveMicRefusedCopy(
    step: MicRefusalStep,
    context: { web: boolean; browser: string; blocker: MicBlocker }
): LiveMicRefusedCopy {
    if (!context.web) {
        if (step === "retry") {
            return {
                title: "Ton micro est bloqué",
                message: frenchSpacing(
                    "Ton masteur ne t'entend pas. Touche « Réessayer » : si ton téléphone te pose la question, choisis « Autoriser »."
                ),
                confirmLabel: "Réessayer",
                cancelLabel: "Plus tard",
                cancel: "later",
            }
        }
        // Au téléphone, rien ne se recharge : on revient des Réglages et on redemande, le retour arrière ferme.
        return {
            title: "Ton téléphone bloque ton micro",
            message: frenchSpacing(
                "Ouvre les Réglages de ton téléphone, puis Masteur, et allume le micro.\nReviens ici et touche « Réessayer »."
            ),
            confirmLabel: "Ouvrir les réglages",
            cancelLabel: "Réessayer",
            cancel: "retry",
        }
    }
    const system = context.blocker === "system"
    // L'ordinateur en cause, sa première étape le dit (« Ton ordinateur bloque le micro… ») : pas deux fois.
    const lines = system ? systemPermissionPath("microphone") : permissionPathFor(context.browser, "microphone")
    return {
        title: "Ton micro est bloqué",
        message: frenchSpacing(
            system
                ? "Pour le débloquer :"
                : `${browserLabel(context.browser) as string} l'a bloqué. Pour le débloquer :`
        ),
        steps: lines.map(toGuidanceLine),
        confirmLabel: "Recharger la page",
        cancelLabel: "Plus tard",
        cancel: "later",
    }
}

/**
 * Le problème que l'écran voit tout seul, dans l'ordre de l'ancien (`useTroubleshootingDetection`) :
 * la connexion faible, puis le micro refusé ou muet, puis la caméra refusée.
 */
export function detectedProblem(
    context: Pick<LiveHelpContext, "poorConnection" | "micAllowed" | "micSilent" | "cameraAllowed">
): UILiveProblem | null {
    if (context.poorConnection) return "connection"
    if (context.micAllowed === false || context.micSilent === true) return "mic"
    if (context.cameraAllowed === false) return "camera"
    return null
}

/* ─────────────────────── le tuteur absent (dix minutes) ─────────────────────── */

/** Le live de remplacement → la ligne de séance de la feuille. */
export function replacementSession(
    dialog: TutorAbsentDialog | null,
    toTutor: (raw: { id?: number; display_name?: string; avatar?: string | null } | null | undefined) => UITutor
): UILiveSession | null {
    const raw = dialog?.replacement
    if (raw == null) return null
    return {
        id: String(raw.id ?? "replacement"),
        date:
            raw.date != null ? moment(raw.date).format("YYYY-MM-DDTHH:mm:ss") : moment().format("YYYY-MM-DDTHH:mm:ss"),
        duration: typeof raw.duration === "number" ? raw.duration : 120,
        tutor: toTutor(raw.tutor ?? null),
        subjects: [],
        statuses: [],
    }
}
